import React, {useEffect, useRef} from 'react';
import {DELETED_STATUS_OPTIONS} from "../../services/constList";
import ReactTooltip from "react-tooltip";

const VIEW_TYPES = { MODULES: 'module', CLUSTERS: 'cluster' };
const RESTORABLE_STATUS = "DELETION_PENDING"

const sortMonthIds = (monthA, monthB) => {
    if (!monthA || !monthB) return 0;
    const [m1, y1] = monthA.split("_");
    const [m2, y2] = monthB.split("_");
    if (y1 !== y2) {
        return parseInt(y1, 10) - parseInt(y2, 10);
    }
    const monthNum1 = m1 ? parseInt(m1.substring(1), 10) : 0;
    const monthNum2 = m2 ? parseInt(m2.substring(1), 10) : 0;
    return monthNum1 - monthNum2;
};

const getDotLabel = (status) => {
    switch (status) {
        case 'DELETED':             return { label: 'Deleted' };
        case 'DELETION_PENDING':    return { label: 'Deletion pending' };
        case 'READY_TO_BE_DELETED': return { label: 'Ready to be deleted' };
        case 'DELETION_IN_PROGRESS':return { label: 'Deletion in progress' };
        case 'DELETION_ERROR':      return { label: 'Deletion error' };
        default:                    return { label: status || 'Unknown' }; // Grey for any other unknown status
    }
};

const getOverallStatusLabel = (statusValue) => {
    const option = DELETED_STATUS_OPTIONS.find(opt => opt.value === statusValue);
    return option ? option.label : statusValue;
};


const RecoveryAreaTable = ({
                               itemIdsOnPage,
                               groupedItems,
                               selectedItemIds,
                               isLoading,
                               activeView,
                               onSelectAll,
                               onRowSelect,
                               restorableItemsIdsOnPage,
                               areAllOnPageSelected,
                               isIndeterminate
                           }) => {

    const getDisplayedMonths = () => {
        if (!itemIdsOnPage || itemIdsOnPage.length === 0) {
            return [];
        }
        const monthSet = new Set();
        itemIdsOnPage.forEach(itemId => {
            const itemData = groupedItems[itemId];
            if (itemData && itemData.months) {
                Object.keys(itemData.months).forEach(monthKey => {
                    if (itemData.months[monthKey] !== null && itemData.months[monthKey].status) { // Ensure there's a status
                        monthSet.add(monthKey);
                    }
                });
            }
        });
        return Array.from(monthSet).sort(sortMonthIds);
    };

    const displayedMonths = getDisplayedMonths();
    const masterCheckboxRef = useRef(null);

    useEffect(() => {
        if (masterCheckboxRef.current) {
            masterCheckboxRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);

    const renderTableHeaders = () => {
        const idHeader = activeView === VIEW_TYPES.MODULES ? "Module Name" : "Cluster ID";
        return (
            <tr>
                <th className="sticky-col first-col">
                    <input
                        ref={masterCheckboxRef}
                        type="checkbox"
                        checked={areAllOnPageSelected}
                        onChange={() => onSelectAll(restorableItemsIdsOnPage, areAllOnPageSelected)}
                        disabled={!restorableItemsIdsOnPage || restorableItemsIdsOnPage.length === 0 || isLoading}
                        aria-label="Select all items on this page"
                        className="my-star-table__checkbox--style"
                    />
                </th>
                <th className="sticky-col second-col">{idHeader}</th>
                {displayedMonths.map(month => (
                    <th key={month}>{month}</th>
                ))}
                <th className="sticky-col last-col">Status</th>
            </tr>
        );
    };

    const renderTableRows = () => {
        if (itemIdsOnPage.length === 0) {
            const colSpan = 2 + displayedMonths.length + 1;
            return (
                <tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>No items found for the selected criteria.</td></tr>
            );
        }

        return itemIdsOnPage.map(itemId => {
            const itemData = groupedItems[itemId];
            if (!itemData) return null;

            const isSelected = selectedItemIds.includes(itemId);
            const overallStatusLabel = getOverallStatusLabel(itemData.overallStatus);
            const isDeletionError = itemData.overallStatus === "DELETION_ERROR";
            const isRestorable = RESTORABLE_STATUS === itemData.overallStatus;
            const errorMessage = isDeletionError ? (itemData.errorMessage || "An error occurred during deletion.") : "";
            const tooltipId = `error-tip-${itemId}`;

            return (
                <tr key={itemId} className={isSelected ? 'selected-row' : ''}>
                    <td className="sticky-col first-col">
                        {isRestorable ? (
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => onRowSelect(itemId)}
                                aria-label={`Select item ${itemData.label}`}
                                className="my-star-table__checkbox--style"
                            />
                        ) : null}
                    </td>
                    <td className="sticky-col second-col">{itemData.label}</td>
                    {displayedMonths.map(month => {
                        const monthCellData = itemData.months[month];
                        const statusInCell = monthCellData ? monthCellData.status : null;
                        const createdAfterInCell = monthCellData? monthCellData.createdAfter : null;
                        const uiVisibleInCell = monthCellData? monthCellData.uiVisible : null;
                        if (!statusInCell) {
                            return <td key={`${itemId}-${month}`}></td>;
                        }

                        let dotColor = '#dc3545'
                        const dotLabel = getDotLabel(statusInCell).label;

                        // const isOverallStatusDeletionRelated = [
                        //     'DELETED', 'DELETION_ERROR', 'DELETION_IN_PROGRESS',
                        //     'DELETION_PENDING', 'READY_TO_BE_DELETED'
                        // ].includes(itemData.overallStatus);

                        if(createdAfterInCell){
                            dotColor = '#ffc107'
                        }
                        else if(uiVisibleInCell){
                            dotColor = '#28a745'
                        }


                        // const pivotMonthString = itemData.overallStatusMonthId;
                        // // else if (isOverallStatusDeletionRelated && pivotMonthString && displayedMonths.includes(pivotMonthString)) {
                        // //     const comparison = sortMonthIds(month, pivotMonthString);
                        // //     if (comparison < 0) {
                        // //         dotColor = '#28a745';
                        // //     }
                        // // }
                        if ((itemData.overallStatus === 'DELETED' && statusInCell) || (itemData.overallStatus === 'READY_TO_BE_DELETED' && statusInCell)) {
                            dotColor = '#dc3545';
                        }
                        const title = `${itemData.label} - ${month}: ${dotLabel}`;

                        return (
                            <td key={`${itemId}-${month}`} title={title}>
                                <span
                                    className="status-dot"
                                    style={{ backgroundColor: dotColor }}
                                ></span>
                            </td>
                        );
                    })}
                    <td className="sticky-col last-col">
                        {isDeletionError && errorMessage &&(
                            <>
                            <span
                                data-for={tooltipId}
                                data-tip={errorMessage}
                                aria-label="Error details"
                            >
                                ⚠️
                            </span>
                            <ReactTooltip
                                id={tooltipId}
                                effect="solid"
                                place = "top"
                                className="recovery-area-page__tooltip--style"
                            />
                            </>
                        )}
                        {overallStatusLabel || 'N/A'}
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="table-container scroll">
            <table className="my-star-table">
                <thead>{renderTableHeaders()}</thead>
                <tbody>{renderTableRows()}</tbody>
            </table>
        </div>
    );
};

export default RecoveryAreaTable;